import { toPanelError } from '@/console/api/errors'

/**
 * 콘솔의 모든 조회·조치가 갖는 4+1 상태.
 * 화면은 이 타입만 알면 되고, 1단계에서는 목데이터가, 2단계에서는 React Query 가 이 값을 만든다.
 * 'forbidden' 을 따로 두는 이유는 @PreAuthorize 거절이 401 로 오기 때문이다(핸드오프 §2.1).
 */
export type PanelState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'empty'; reason: string }
  | { status: 'error'; kind: 'forbidden' | 'other'; message: string }
  | { status: 'ready'; data: T }

export const idle = <T>(): PanelState<T> => ({ status: 'idle' })
export const loading = <T>(): PanelState<T> => ({ status: 'loading' })
export const empty = <T>(reason: string): PanelState<T> => ({ status: 'empty', reason })
export const ready = <T>(data: T): PanelState<T> => ({ status: 'ready', data })
export const isReady = <T>(s: PanelState<T>): s is { status: 'ready'; data: T } => s.status === 'ready'

/**
 * LOGISPRD-12506 Task 13 — React Query 의 `UseQueryResult` 를 `PanelState<T>` 로 접는 공통
 * 변환기. Task 13~18 전부가 이걸 통과하므로 분기 순서가 중요하다:
 *  1. `isFetching` 이 최우선이다 — refetch 중엔 이전 data/error 가 아직 남아 있을 수 있어서(React
 *     Query 는 성공 이력이 있으면 refetch 동안도 이전 data 를 유지한다), data 유무보다 먼저 본다.
 *  2. 에러는 `toPanelError` 로 401(권한 부족) 과 그 외를 가른다(핸드오프 §2.1).
 *  3. 아직 한 번도 fetch 가 끝나지 않았거나(`!isFetched`) data 가 없으면 `idle` — 콘솔은
 *     `enabled:false` 라 사용자가 조회를 실행하기 전엔 이 상태에 머문다.
 *  4. 그 다음에야 `isEmpty` 로 empty/ready 를 가른다.
 *
 * 인자를 `UseQueryResult` 전체가 아니라 필요한 필드만 받는 구조적 타입으로 잡은 건 테스트에서
 * react-query 객체를 통째로 흉내 내지 않고 값만으로 5분기를 단언할 수 있게 하기 위해서다.
 */
export const toPanelState = <T>(
  q: { isFetching: boolean; isError: boolean; error: unknown; data: T | undefined; isFetched: boolean },
  emptyReason: string,
  isEmpty: (d: T) => boolean,
): PanelState<T> => {
  if (q.isFetching) {
    return { status: 'loading' }
  }

  if (q.isError) {
    return { status: 'error', ...toPanelError(q.error) }
  }

  if (!q.isFetched || q.data === undefined) {
    return { status: 'idle' }
  }

  return isEmpty(q.data) ? { status: 'empty', reason: emptyReason } : { status: 'ready', data: q.data }
}
